<script>
import Breadcrumbs from '@/components/MarketplaceComponents/Breadcrumbs.vue'
import PageTitle from '@/components/MarketplaceComponents/PageTitle.vue'
import CheckoutShort from '@/components/MarketplaceComponents/CheckoutShort.vue'
import SellerCard from '@/components/MarketplaceComponents/SellerCard.vue'
import ReviewsBlock from '@/components/MarketplaceComponents/ReviewsBlock.vue'
import ProductCard from '@/components/MarketplaceComponents/ProductCard'
import ComicsCard from '@/components/MarketplaceComponents/ComicsCard'
// import Tooltip from '@/components/Tooltip/Tooltip.vue'
import StoreService from '@/services/StoreService'
import SeoService from '@/services/SeoService'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { copyText, escapeHtml } from '~/utils/utils'
import { createMeta } from '~/utils/meta'

const REF_LINK_REGEXP = /^([a-zA-Z0-9 _-]+)$/

export default {
  components: {
    ComicsCard,
    // Tooltip,
    SellerCard,
    Breadcrumbs,
    PageTitle,
    CheckoutShort,
    ReviewsBlock,
    ProductCard
  },

  async asyncData ({ route, i18n, $axios, $config }) {
    const product = (await StoreService.getMarketplace($axios, route.params.id))?.data?.data

    let sameProducts = null
    let productCover = null

    let seoData

    const lang = i18n.locale.includes('en') ? 'en' : i18n.locale

    try {
      seoData = (await SeoService.getPageSeo($axios, lang, $config.WWW_HOST + route.path))?.data?.data?.seo_data
    } catch (e) {
      console.log('error in getPageSeo:', e)
    }

    try {
      productCover = product.images[0]
    } catch (e) {
      console.log('error in cover:', e)
    }

    try {
      if (product?.brand === 'Nutaku' || product?.brand === 'Comics') {
        const { data } = await StoreService.getMarketplaceProductsWithUserID($axios, 1, 5, 5418)

        sameProducts = data?.data?.products.filter(p => p.id !== product.id)
      } else {
        const { data } = await StoreService.getMarketplaceProductsWithUserID($axios, 1, 5, product?.application_category_id)
        sameProducts = data?.data?.products.filter(p => p.id !== product.id)
      }
    } catch (e) {
      console.log(e)
    }

    return {
      product,
      productCover,
      sameProducts,
      seoData
    }
  },

  data () {
    return {
      product: undefined,
      payMethod: '',
      showButton: true,
      refLinks: null,
      promocodes: null,
      isCopied: false,
      sameProducts: null,
      productCover: null,
      seoData: undefined
    }
  },

  computed: {
    ...mapState('user', ['user', 'orders', 'sales']),
    ...mapGetters('user', ['isAuthorized', 'isAdmin']),

    payMethods () {
      return [
        {
          title: `${this.$t('creditCard')}`,
          commissionText: '3.3% +0.25€',
          value: 'BANKCARD',
          commission: 3.3,
          commissionFix: 25,
          icon: 'usd-card',
          euOnly: true
        },
        {
          title: `${this.$t('ruBankCard')}`,
          commissionText: '4%',
          value: 'CONTACTPAY', // this.isAdmin ? 'CONTACTPAY' : 'YOURPAYMENT', // P2P
          commission: 4,
          commissionFix: 0,
          icon: 'ru-card',
          ruOnly: true
        },
        {
          title: this.$t('siteBalance'),
          commissionText: '0%',
          value: 'BALANCE',
          commission: 0,
          commissionFix: 0,
          icon: 'V-round'
        }
      ]
    },

    locale () {
      if (this.$i18n.locale === 'eo') {
        return 'ru'
      }
      return this.$i18n.locale?.split?.('-')[0] || 'en'
    },

    productId () {
      return this.$route.params.id
    },

    productTitle () {
      return `${this.product?.app?.title} ${this.$t(this.product?.category?.category_name)}`
    },

    h1 () {
      if (this.seoData?.h1 && this.seoData?.h1 !== '') {
        return this.seoData.h1
      }
      return this.productTitle
    },

    productDealer () {
      return this.product?.partner
    },

    productSpecs () {
      return this.product?.specifications
    },

    productIsHidden () {
      return this.product?.hidden
    },

    productDescriptionHtml () {
      return (this.product?.seo_data?.[this.locale]?.description || this.product?.description || '').replace(/(?:\r\n|\r|\n)/g, '<br />')
    },

    productShortDescription () {
      return this.product?.seo_data?.[this.locale]?.h1 || this.product?.short_description || ''
    },

    productShortDescriptionHtml () {
      return escapeHtml(this.productShortDescription).replace(/(?:\r\n|\r|\n)/g, '<br />')
    },

    isSeller () {
      return this.user?.id === this.product?.account_id
    },

    refAmount () {
      return (Math.floor(this.product?.max_price - this.product?.min_price) / 100).toFixed(2).replace('.00', '')
    },

    breadcrumbs () {
      return [
        { title: this.$t('marketplace'), to: '/marketplace' },
        { title: this.product?.app?.title, to: `/marketplace/${this.product?.app?.seo_data?.path}` },
        { title: this.$t(this.product?.category?.category_name), to: `/marketplace/${this.product?.app?.seo_data?.path}/${this.product?.category?.seo_data?.path}` },
        { title: this.productTitle }
      ]
    },

    goBackPath () {
      return `/marketplace/${this.product?.app?.seo_data?.path}/${this.product?.category?.seo_data?.path}`
    },

    commission () {
      return (this.payMethods?.find(m => m.value === this.payMethod)?.commission || 0) / 100
    },

    commissionFix () {
      return this.payMethods.find(m => m.value === this.payMethod)?.commissionFix || 0
    },

    finalPriceWithCommission () {
      return (Math.ceil((this.product?.max_price) * (1 + this.commission) + this.commissionFix) / 100).toFixed(2)
    },

    totalCommission () {
      return (Math.ceil((this.product?.max_price) * (this.commission) + this.commissionFix) / 100).toFixed(2)
    },

    commissionText () {
      return this.payMethods.find(m => m.value === this.payMethod)?.commissionText || '...'
    },

    payMethodsToSelect () {
      return this.payMethods
        .filter(method => !method.ruOnly || this.$i18n.locale === 'ru')
        .filter(method => !method.euOnly || this.$i18n.locale !== 'ru')
        .map((m) => {
          const commission = m.commission / 100
          const commissionFix = m.commissionFix
          const finalPriceWithCommission = (Math.ceil((this.product?.max_price) * (1 + commission) + commissionFix) / 100).toFixed(2)

          return {
            ...m,
            money: finalPriceWithCommission
          }
        })
    },

    promocode () {
      return this.promocodes?.[0]
    },

    refLink () {
      return this.refLinks?.[0]
    },

    tooltipTitle () {
      return this.isCopied ? this.$t('isCopied') : this.$t('copyInput')
    },

    isFavourite () {
      return this.product?.favourite
    },

    bannerBackgroundMobile () {
      return this.$i18n.locale === 'ru' ? require('@/assets/img/marketplace/best-price-ru-m.webp') : require('@/assets/img/marketplace/best-price-en-m.webp')
    },

    bannerBackground () {
      return this.$i18n.locale === 'ru' ? require('@/assets/img/marketplace/best-price-ru-d.webp') : require('@/assets/img/marketplace/best-price-en-d.webp')
    },

    variantsField () {
      return this.product?.fields?.find(f => f?.type === 'variants')
    },

    variantsFieldOptions () {
      return this.variantsField?.options
    },

    defaultProductOption () {
      return {
        title: this.product?.title,
        id: this.product?.id,
        max_price: this.product?.max_price,
        min_price: this.product?.min_price,
        r_price: this.product?.r_price,
        quantity: this.product?.quantity
      }
    },

    productOptions () {
      return this.product?.options || [this.defaultProductOption]
    },

    productOffers () {
      if (this.product?.account_id === 839816) {
        return this.productOptions?.filter(opt => opt.id !== this.product?.id)
      }

      return undefined
    },

    options () {
      const opts = this.product?.account_id === 839816 ? [this.defaultProductOption] : this.productOptions
      if (opts.length === 1) {
        return opts.map(o => ({
          ...o,
          product: this.product
        }))
      }
      const findFunc = title => po => po?.title?.startsWith(title)
      return this.variantsFieldOptions?.map((o) => {
        const title = '[' + (o?.title || '') + ']'
        return {
          ...o,
          product: opts?.find(findFunc(title))
        }
      })
    },

    onSale () {
      if (this.product?.account_id === 839816) {
        return !this.product?.hidden
      }
      return (!this.product?.hidden) && this.options
        ?.map(o => o.product?.quantity || 0)
        ?.reduce((a, b) => +a + +b, 0)
    },

    isRu () {
      return this.$i18n.locale === 'ru'
    }
  },

  watch: {
    payMethod () {
      this.$gtag.event('pay_method', { account_id: this.user?.id })
      this.$gtag.event(`pay_method_${this.payMethod.toLowerCase()}`, { account_id: this.user?.id })
    },

    isAuthorized () {
      if (this.isAuthorized) {
        this.updateRefLinks()
      } else {
        this.refLinks = null
        this.promocodes = null
      }
    }
  },

  async mounted () {
    const adultCookie = this.$cookiz.get('adultQuestionAnswer')

    if (this.product?.category?.application_id === 1061 && !adultCookie) {
    // if (!adultCookie) {
      this.showModal({
        component: 'ModalMessage',
        canClose: false,
        data: {
          type: 'info',
          text: this.$t('adultContentInfo'),
          title: this.$t('adultContentInfoTitle'),
          buttons: [
            {
              props: { isPrimary: true },
              title: this.$t('adultContentInfoYes'),
              shouldClose: false,
              action: () => {
                this.$cookiz.set('adultQuestionAnswer', 1)
                this.toggle(false)
              }
            },
            {
              props: { isSecondary: true },
              title: this.$t('adultContentInfoNo'),
              shouldClose: false,
              action: () => {
                this.$cookiz.set('adultQuestionAnswer', null)
                this.$router.push(this.localeLocation('/marketplace'))
                this.toggle(false)
              }
            }
          ]
        }
      })
    }

    if (this.isAuthorized) {
      this.updateRefLinks()
    }

    if (!this.orders?.length) {
      await this.fetchOrders()
    }

    if (!this.sales?.length) {
      await this.fetchOrdersAsSeller()
    }

    if (this.$route.query.order_id) {
      const order = [...(this.orders || []), ...(this.sales || [])].find(o => o.id === +this.$route.query.order_id)
      if (order) {
        this.setChatName(`${this.$t('order')} #${order.id}`)
        this.setChatId(`${[order.account_id, order.seller_id].sort().join('_')}`)
      }
    }

    try {
      const lastViewed = window.localStorage.getItem('viewedItems')
      const newViewed = [this.product]
      if (lastViewed?.length) {
        const newElements = JSON.parse(lastViewed)?.filter(e => e.id !== this.product?.id)
        newViewed.push(...newElements)
      }
      window.localStorage.setItem('viewedItems', JSON.stringify(newViewed.slice(0, 4)))
    } catch (e) {
      console.log(e)
    }

    try {
      if (!this.onSale) {
        setTimeout(() => {
          this.$router.push(this.localeLocation(`/marketplace/${this.product?.app?.seo_data?.path}/${this.product?.category?.seo_data?.path}`))
        }, 6000)
      }
    } catch (e) {
      console.log(e)
    }
  },

  methods: {
    ...mapMutations('chat', ['setChatId', 'setChatName']),
    ...mapMutations('modal', ['toggle']),
    ...mapActions('modal', ['showModal']),
    ...mapActions('user', ['fetchOrders', 'fetchOrdersAsSeller']),

    async loadProduct () {
      this.product = (await StoreService.getMarketplace(this.$axios, this.productId))?.data?.data
      this.productCover = this.product.images[0]

      this.loadSameProducts()
    },

    setCover (img) {
      this.productCover = img
    },

    alert (str) {
      alert(str)
    },

    async loadSameProducts () {
      if (this.product?.brand === 'Nutaku' || this.product?.brand === 'Comics') {
        const { data } = await StoreService.getMarketplaceProductsWithUserID(this.$axios, 1, 6, 5418)

        this.sameProducts = data?.data?.products.filter(p => p.id !== this.product.id)
      } else {
        const { data } = await StoreService.getMarketplaceProductsWithUserID(this.$axios, 1, 6, this.product?.application_category_id)

        this.sameProducts = data?.data?.products.filter(p => p.id !== this.product.id)
      }
    },

    addToFavourites () {
      if (!this.isAuthorized) {
        this.openAuthModal()
      } else if (this.isFavourite) {
        StoreService.dropFavourite(this.$axios, this.product?.id)
        this.product.favourite = false
      } else {
        StoreService.addToFavourites(this.$axios, this.product?.id)
        this.product.favourite = true
      }
    },

    getFullLink (link) {
      const host = this.$config.WWW_HOST.replace('https://dev.', '')

      return `<span>${host}?ref=</span>${link}`
    },

    async updateRefLinks () {
      this.$nuxt.$loading.start?.()

      try {
        const { data } = await this.$axios.get('/referrals/refs')
        this.refLinks = data?.data?.map(link => link.ref).filter(ref => !ref.includes('promocode:'))
        this.promocodes = data?.data?.map(link => link.ref).filter(ref => ref.includes('promocode:')).map(ref => ref.replace('promocode:', ''))
      } catch (e) {
        console.error(
          `${new Date().toUTCString()} :: ref links fetching error ::`,
          e
        )
      }

      this.$nuxt.$loading.finish?.()

      if (!this.refLinks?.length) {
        this.onNewLinkClick()
      }

      if (!this.promocodes?.length) {
        this.onNewPromoCodeClick()
      }
    },

    async onNewPromoCodeClick () {
      this.$nuxt.$loading.start?.()

      try {
        await this.$axios.get('/promocode/new')

        await this.updateRefLinks()
      } catch (e) {
        console.error(
          `${new Date().toUTCString()} :: new promo code adding error ::`,
          e
        )

        this.showModal({
          component: 'ModalMessage',
          data: { text: this.$errors.getText(e) }
        })
      }

      this.$nuxt.$loading.finish?.()
    },

    async onNewLinkClick () {
      const ref = this.newLinkTitle
      const invalidTitle = !REF_LINK_REGEXP.test(ref)
      const alreadyHave = this.refLinks.includes(ref)

      let error = ''

      if (invalidTitle) {
        error = this.$t('incorrectRefLinkTitle')
      } else if (alreadyHave) {
        error = this.$t('alreadyHaveLink')
      }

      if (error.length) {
        return this.showModal({
          component: 'ModalMessage',
          data: { type: 'error', text: error }
        })
      }

      this.$nuxt.$loading.start?.()

      try {
        await this.$axios.post('/referrals', { ref })

        await this.updateRefLinks()
        this.newLinkTitle = ''
      } catch (e) {
        console.error(
          `${new Date().toUTCString()} :: new ref link adding error ::`,
          e
        )

        this.showModal({
          component: 'ModalMessage',
          data: { text: this.$errors.getText(e) }
        })
      }

      this.$nuxt.$loading.finish?.()
    },

    onCopyLinkClick (link, promocode = false) {
      this.isCopied = true
      let text = `${this.$config.WWW_HOST}?ref=${link}`

      if (promocode) {
        text = link
      }

      copyText(text)

      setTimeout(() => {
        this.isCopied = false
      }, 5e3)
    },

    openAuthModal () {
      this.showModal({
        component: 'Auth',
        data: {
          mode: 'register'
        }
      })
    },

    openRefPage () {
      if (!this.isAuthorized) {
        this.openAuthModal()
        return
      }

      this.$router.push(this.localeLocation('/profile/ref'))
    },

    editProduct () {
      this.showModal({
        component: 'EditProductModal',
        data: {
          productId: this.product?.id,
          updated: this.loadProduct
        }
      })
    },

    async approveProduct () {
      if (confirm('Are you sure?')) {
        await StoreService.moderateProduct(this.$axios, this.product?.id)
        this.$router.push(this.localeLocation('/profile/goods-moderation'))
      }
    },

    async deleteProduct () {
      if (confirm('Are you sure ?')) {
        await StoreService.deleteProduct(this.$axios, this.product?.id)
        this.$router.push(this.localeLocation('/profile/goods-moderation'))
      }
    },

    async recoverProduct () {
      if (confirm('Are you sure ?')) {
        await StoreService.recoverProduct(this.$axios, this.product?.id)
        this.$router.push(this.localeLocation('/profile/goods-moderation'))
      }
    }
  },

  head () {
    const seo = this.product?.seo_data?.[this.locale]
    // const productSeo = this.product?.seo_data?.[this.locale]

    if (this.locale === 'ru') {
      let title = this.seoData?.title || this.product?.seo_data?.ru?.h1 || this.product?.seo_data?.en?.h1 || ''

      if (title.length > 58) {
        title = title.slice(0, 55) + '...'
      }

      // let description
      // if (this.seoData?.description) {
      //   description = this.seoData?.description // || productSeo?.description || this.product?.description || '')
      // }
      //
      // if (description && description?.length > 160) {
      //   description = description.slice(0, 157) + '...'
      // }

      const resultObject = {
        title,
        description: ' ',
        sharingTitle: title,
        sharingDescription: ' '
      }

      if (this.productCover) {
        resultObject.image = this.productCover
      }

      return createMeta.call(this, resultObject)
    } else if (this.product?.account_id === 839816) {
      let title = this.$t('kinguinMetaTitle', { app: this.product?.brand || '', category: this.product?.category?.category_name || '' })
      if (title.length > 60) {
        title = title.replace('for ', '')
      }
      return createMeta.call(this, {
        title,
        description: ' ',
        sharingTitle: title,
        sharingDescription: ' '
      })
    } else if (seo) {
      let title = this.seoData?.title || seo.h1 || undefined

      if (title?.length > 58) {
        title = title.slice(0, 55) + '...'
      }

      // let description = escapeHtml(this.seoData?.description || productSeo?.description || '')
      //
      // if (description.length > 160) {
      //   description = description.slice(0, 157) + '...'
      // }

      return createMeta.call(this, {
        title,
        description: ' ',
        sharingTitle: title,
        sharingDescription: ' '
      })
    } else {
      const title = this.product?.title || ''

      return createMeta.call(this, {
        title,
        description: ' ',
        sharingTitle: title,
        sharingDescription: ' '
      })
    }
  }
}
</script>

<template>
  <div :class="$style.page">
    <Breadcrumbs :list="breadcrumbs" />

    <div v-if="product" :class="$style.page__body">
      <div :class="$style.title">
        <PageTitle with-go-back :go-back-path="goBackPath" :title="h1" />
        <div :class="$style.likeContainer" @click="addToFavourites">
          <img v-if="!isFavourite" :class="$style.like" :src="require('~/assets/img/icons/heart.svg')" alt="like" />
          <img v-else :class="$style.like" :src="require('~/assets/img/icons/heart-active.svg')" alt="unlike" />
        </div>
      </div>

      <div v-if="!isRu" style="display: inline-block; background: white; margin: -9px 0; padding: 4px 8px; width: max-content; border-radius: 4px">
        <div
          class="trustpilot-widget"
          data-locale="en-US"
          data-template-id="5419b732fbfb950b10de65e5"
          data-businessunit-id="66547c80598c1c220e3925a7"
          data-style-height="24px"
          data-style-width="100%"
          data-theme="light"
        >
          <a href="https://www.trustpilot.com/review/gaming-goods.com" target="_blank" rel="noopener">Trustpilot</a>
        </div>
      </div>
      <!--      <div v-if="isRu" style="display: inline-block; background: #ce2457; margin: 16px 3px; padding: 4px 8px; width: max-content; border-radius: 4px">-->
      <!--        <a href="https://otzovik.com/reviews/ru_gaming-goods_com-internet-magazin_igr/" target="_blank" rel="noopener">Otzovik</a>-->
      <!--      </div>-->
      <!--      <div v-if="isRu" style="display: inline-block; background: white; margin: 16px 0; padding: 4px 8px; width: max-content; border-radius: 4px">-->
      <!--        <a href="https://yandex.ru/profile/126275679012" target="_blank" rel="noopener" style="color: red;">Yandex</a>-->
      <!--      </div>-->

      <!--      <div v-if="!onSale">-->
      <!--        <div :class="$style.finished">-->
      <!--          <div :class="$style.emptyStocks">-->
      <!--            <img :src="require(`~/assets/img/icons/sad.svg`)" :class="$style.notFound__icon" alt="empty" />-->
      <!--            <div :class="$style.emptyStocks__info">-->
      <!--              <p>-->
      <!--                {{ $t('itemOutOfStock') }}-->
      <!--              </p>-->
      <!--              <p :class="$style.emptyStocks__text">-->
      <!--                {{ $t('itemOutOfStockAdditional') }}-->
      <!--              </p>-->
      <!--            </div>-->
      <!--          </div>-->
      <!--          <SellerCard :product="product" no-chat />-->
      <!--        </div>-->
      <!--        <div v-if="sameProducts && sameProducts.length" :class="$style.sameProducts" :style="{'&#45;&#45;amount': sameProducts.length}">-->
      <!--          <p :class="$style.sameProducts__title">-->
      <!--            {{ $t('sameProducts') }}-->
      <!--          </p>-->
      <!--          <div v-for="sProduct in sameProducts" :key="sProduct.id">-->
      <!--            <ProductCard :product="sProduct" short :with-category-text="false" with-seller-text />-->
      <!--          </div>-->
      <!--        </div>-->
      <!--      </div>-->

      <div :class="$style.page__bodyMain">
        <div :class="$style.page__bodyMainContainer">
          <template v-if="product?.short_description">
            <div :class="$style.description">
              <template v-if="product?.images?.length">
                <img :src="productCover" alt="cover" :class="$style.cover" />
                <div v-if="product?.images?.length > 1" :class="$style.covers">
                  <img
                    v-for="image in product.images"
                    :key="image"
                    :src="image"
                    alt="image"
                    :class="$style.image"
                    @click="setCover(image)"
                  />
                </div>
              </template>
              <div :class="$style.description__header">
                {{ $t('shortDescription') }}
              </div>
              <div :class="$style.description__body" v-html="productShortDescriptionHtml" />
            </div>
          </template>

          <SellerCard :class="$style.mobile" :product="product" />

          <div v-if="isSeller || isAdmin" :class="$style.mobile">
            <Button :class="$style.edit" is-secondary @click="editProduct">
              {{ $t('edit') }}
            </Button>
          </div>

          <div v-if="isAdmin" :class="$style.mobile">
            <Button :class="$style.edit" is-primary @click="approveProduct">
              {{ $t('approve') }}
            </Button>
          </div>

          <div v-if="isAdmin && !productIsHidden" :class="$style.mobile">
            <Button :class="$style.edit" is-primary is-red @click="deleteProduct">
              {{ $t('delete') }}
            </Button>
          </div>

          <div v-else-if="isAdmin" :class="$style.mobile">
            <Button :class="$style.edit" is-primary is-red @click="recoverProduct">
              {{ $t('recover') }}
            </Button>
          </div>

          <CheckoutShort
            v-model="payMethod"
            :class="[$style.checkout, $style.mobile]"
            :product="product"
            :pay-methods="payMethodsToSelect"
            :final-price-with-commission="finalPriceWithCommission"
            :total-commission="totalCommission"
            :commission-text="commissionText"
            :show-button="showButton"
            :is-seller="isSeller"
            :on-sale="onSale > 0"
            with-options
            :product-offers="productOffers"
            @buy="() => {}"
          />

          <div :class="$style.description">
            <!--            <div :class="$style.description__header">-->
            <!--              {{ $t('general') }}-->
            <!--            </div>-->
            <!--            <div :class="$style.description__body">-->
            <!--              <div :class="$style.specs">-->
            <!--                <div v-for="spec in productSpecs" :key="spec.key" :class="$style.spec">-->
            <!--                  <span :class="$style.spec__key">{{ spec.key }}</span>-->
            <!--                  <span :class="$style.spec__value">{{ spec.value }}</span>-->
            <!--                </div>-->
            <!--              </div>-->
            <!--            </div>-->

            <div :class="$style.description__header">
              {{ $t('description') }}
            </div>
            <div :class="$style.description__body" v-html="productDescriptionHtml" />
          </div>

          <!--          <div :class="$style.referral">-->
          <!--            <div :class="$style.referral__header">-->
          <!--              <span :class="$style.referral__title">{{ $t('inviteFriend') }}</span>-->
          <!--              <img :class="$style.referral__icon" :src="require('~/assets/img/icons/angle-right.svg')" alt="angle right" @click="openRefPage" />-->
          <!--            </div>-->

          <!--            <div :class="$style.referral__body">-->
          <!--              <div :class="$style.referral__main">-->
          <!--                <Currency type="money" real>-->
          <!--                  <span :class="$style.referral__amount">-->
          <!--                    {{ refAmount }}-->
          <!--                  </span>-->
          <!--                </Currency>-->
          <!--                <span :class="$style.referral__text">{{ $t('refPaymentYouWillReceive') }}</span>-->
          <!--              </div>-->

          <!--              <div :class="$style.referral__links">-->
          <!--                <div v-if="promocode" :class="$style.referral__link">-->
          <!--                  <div :class="$style.referral__linkLabel">-->
          <!--                    {{ $t('yourPromocodeIs') }}-->
          <!--                  </div>-->
          <!--                  <div :class="$style.referral__linkField" v-html="promocode" />-->
          <!--                  <Tooltip :class="$style.referral__tooltip">-->
          <!--                    <img :class="$style.referral__linkIcon" :src="require('~/assets/img/icons/copy.svg')" alt="copy" @click="onCopyLinkClick(promocode)" />-->

          <!--                    <template v-slot:tooltip>-->
          <!--                      <div :class="$style.referral__tooltipDescription">-->
          <!--                        <img v-if="isCopied" :class="$style.referral__tooltipDescriptionIcon" :src="require('~/assets/img/icons/checkmark-green-plain.svg')" alt="checkmark" />-->
          <!--                        <span :class="$style.referral__tooltipDescriptionText">-->
          <!--                          {{ tooltipTitle }}-->
          <!--                        </span>-->
          <!--                      </div>-->
          <!--                    </template>-->
          <!--                  </Tooltip>-->
          <!--                </div>-->

          <!--                <div v-if="refLink" :class="$style.referral__link">-->
          <!--                  <div :class="$style.referral__linkLabel">-->
          <!--                    {{ $t('linkInvite') }}-->
          <!--                  </div>-->
          <!--                  <div :class="$style.referral__linkField" v-html="getFullLink(refLink)" />-->
          <!--                  <Tooltip :class="$style.referral__tooltip">-->
          <!--                    <img :class="$style.referral__linkIcon" :src="require('~/assets/img/icons/copy.svg')" alt="copy" @click="onCopyLinkClick(refLink)" />-->

          <!--                    <template v-slot:tooltip>-->
          <!--                      <div :class="$style.referral__tooltipDescription">-->
          <!--                        <img v-if="isCopied" :class="$style.referral__tooltipDescriptionIcon" :src="require('~/assets/img/icons/checkmark-green-plain.svg')" alt="checkmark" />-->
          <!--                        <span :class="$style.referral__tooltipDescriptionText">-->
          <!--                          {{ tooltipTitle }}-->
          <!--                        </span>-->
          <!--                      </div>-->
          <!--                    </template>-->
          <!--                  </Tooltip>-->
          <!--                </div>-->
          <!--              </div>-->
          <!--            </div>-->
          <!--          </div>-->

          <div :class="$style.mobile">
            <div :class="$style.banner">
              <picture :class="$style.banner__image">
                <source :srcset="bannerBackground" type="image/webp" media="(min-width: 768px)" />
                <source :srcset="bannerBackgroundMobile" type="image/webp" />
                <img :src="bannerBackgroundMobile" alt="background" />
              </picture>
            </div>
          </div>

          <ReviewsBlock :class="$style.mg12" :product="product" />
        </div>

        <div>
          <div :class="$style.right">
            <!--  v-if="onSale > 0"-->
            <SellerCard :product="product" />

            <div v-if="isSeller || isAdmin" :class="$style.desktop">
              <Button :class="$style.edit" is-secondary @click="editProduct">
                {{ $t('edit') }}
              </Button>
            </div>

            <div v-if="isAdmin" :class="$style.desktop">
              <Button :class="$style.edit" is-primary @click="approveProduct">
                {{ $t('approve') }}
              </Button>
            </div>

            <div v-if="isAdmin" :class="$style.desktop">
              <Button :class="$style.edit" is-primary is-red @click="deleteProduct">
                {{ $t('delete') }}
              </Button>
            </div>

            <div :class="$style.desktop">
              <div :class="$style.banner">
                <picture :class="$style.banner__image">
                  <source :srcset="bannerBackground" type="image/webp" media="(min-width: 768px)" />
                  <source :srcset="bannerBackgroundMobile" type="image/webp" />
                  <img :src="bannerBackgroundMobile" alt="background" />
                </picture>
              </div>
            </div>

            <CheckoutShort
              v-model="payMethod"
              :product="product"
              :pay-methods="payMethodsToSelect"
              :final-price-with-commission="finalPriceWithCommission"
              :total-commission="totalCommission"
              :commission-text="commissionText"
              :show-button="showButton"
              :is-seller="isSeller"
              :on-sale="onSale > 0"
              with-options
              :product-offers="productOffers"
              @buy="() => {}"
            />
          </div>
        </div>
      </div>

      <template>
        <div v-if="sameProducts && sameProducts.length" :class="$style.sameProducts" :style="{'--amount': sameProducts.length}">
          <p :class="$style.sameProducts__title">
            {{ $t('sameProducts') }}
          </p>
          <div v-for="sProduct in sameProducts" :key="sProduct.id">
            <ComicsCard v-if="product.brand === 'Nutaku'" :product="sProduct" />
            <ProductCard v-else :product="sProduct" short :with-category-text="false" with-seller-text />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" module>
.page {
  width: 100%;

  &__body {
    padding: 16px;

    &Main {
      @media (min-width: $breakpointDesktopWide) {
        display: grid;
        grid-template-columns: 1fr 496px;
        grid-gap: 24px;
        margin: 40px 0;
      }

      &Container {
        overflow: hidden;
      }
    }
  }
}

.finished {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 12px;

  @media (min-width: $breakpointDesktopWide) {
    display: grid;
    grid-template-columns: 1fr 496px;
    grid-gap: 24px;
    margin: 40px 0;
  }
}

.emptyStocks {
  display: grid;
  grid-template-columns: 48px 1fr;
  grid-gap: 16px;
  align-items: center;
  background: $blue-grey90;
  padding: 16px;
  border-radius: 8px;
  color: $white;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;

  @media (min-width: $breakpointDesktopWide) {
    padding: 24px 32px;
    grid-gap: 24px;
  }

  &__text {
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
  }

  &__info {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 16px;
    margin-left: 16px;
  }
}

.right {
  display: none;

  @media (min-width: $breakpointDesktopWide) {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 24px;
  }
}

.title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.likeContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  background: $blue-grey60;
  border-radius: 8px;
  height: 40px;
  width: 40px;
  margin-left: 12px;
  cursor: pointer;
}

.like {
  height: 24px;
  width: 24px;

  &Active {
    path {
      fill: $primary60;
    }
  }
}

.description {
  width: 100%;
  margin: 24px 0;
  color: $white;
  background: $blue-grey70;
  border-radius: 8px;

  @media (min-width: $breakpointDesktopWide) {
    margin: 0;
  }

  &__header {
    padding: 16px;
    border-bottom: 1px solid $blue-grey40;
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;

    @media (min-width: $breakpointDesktopWide) {
      padding: 32px;
    }
  }

  &__body {
    padding: 20px 16px;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    border-bottom: 1px solid $blue-grey40;

    &:last-child {
      border: none;
    }

    @media (min-width: $breakpointDesktopWide) {
      padding: 32px;
    }
  }
}

.referral {
  width: 100%;
  margin: 24px 0;
  color: $white;
  background: $blue-grey70;
  border-radius: 8px;

  &__header {
    padding: 16px;
    display: grid;
    grid-template-columns: 1fr 24px;
    grid-gap: 8px;
    border-bottom: 1px solid $blue-grey40;
  }

  &__title {
    color: $white;
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
  }

  &__icon {
    width: 24px;
    height: 24px;
    cursor: pointer;
  }

  &__body {
    padding: 16px;
  }

  &__main {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    background: $blue-grey80;
    border: 1px solid $blue-grey40;
    border-radius: 8px;
  }

  &__amount {
    color: $white;
    margin-bottom: 8px;
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
  }

  &__text {
    color: $blue-grey05;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  &__links {
    margin-top: 20px;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 8px;
    align-items: center;

    @media (min-width: $breakpointTablet) {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__link {
    display: grid;
    grid-template-columns: 1fr 48px;
    grid-gap: 8px;
    align-items: center;

    &Label {
      grid-column: 1 / -1;
      color: $blue-grey10;
      font-size: 14px;
      font-weight: 400;
      line-height: 100%;
      text-transform: uppercase;
    }

    &Field {
      padding: 14px 16px;
      color: $white;
      background: $blue-grey60;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &Icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      background: $blue-grey60;
      border-radius: 8px;
      padding: 12px;
    }
  }

  &__tooltipDescription {
    background: $blue-grey60;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid $blue-grey40;
    color: $white;
  }
}

.specs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 16px;
  align-items: center;
}

.spec {
  display: block;
  width: 100%;

  &__key {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    color: $blue-grey10;
    font-size: 10px;
    font-weight: 400;
    line-height: 14px;
    letter-spacing: 0.2px;
    text-transform: uppercase;
  }

  &__value {
    color: $white;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
}

.checkout {
  margin: 24px 0;
}

.mobile {
  @media (min-width: $breakpointDesktopWide) {
    display: none;
  }
}

.desktop {
  display: none;

  @media (min-width: $breakpointDesktopWide) {
    display: block;
  }
}

.edit {
  width: 100%;
  min-width: 100% !important;
  max-width: 100% !important;
  margin: 16px 0;

  @media (min-width: $breakpointDesktopWide) {
    margin: 0;
  }
}

.banner {
  display: block;
  width: 100%;
  margin: 20px 0;
  border-radius: 16px;
  background-color: $blue-grey70;
  overflow: hidden;

  @media (min-width: $breakpointTablet) {
    margin: 0;
  }

  &__image {
    display: block;
    width: 100%;

    img {
      width: 100%;
    }

    &Mob {
      display: block;
      width: 100%;

      @media (min-width: $breakpointTablet) {
        display: none;
      }
    }
  }
}

.sameProducts {
  @include silent-scroll;

  display: grid;
  grid-template-columns: repeat(var(--amount), 328px);
  grid-gap: 8px;
  width: 100%;
  min-width: 160px;
  margin: 20px 0;
  border-radius: 10px;
  overflow-x: auto;
  //padding-bottom: 10px;
  padding: 20px 16px;
  background: $blue-grey60;

  @media (min-width: $breakpointDesktopWide) {
    grid-template-columns: repeat(5, 1fr);
    grid-gap: 16px;
  }

  &__title {
    width: 100%;
    color: $white;
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    margin: 0 0 12px;
    grid-column: 1 / -1;
  }
}

.cover {
  margin: 12px 0;
  width: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.covers {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: 8px;
  margin: 0 0 12px;
  padding: 8px;
  border-radius: 8px;
  background: $blue-grey70;
}

.image {
  width: 100%;
  max-width: 100%;
  height: 40px;
  border-radius: 8px;
  border: 1px solid $blue-grey40;
  object-fit: cover;
  cursor: pointer;

  @media (min-width: $breakpointDesktopWide) {
    height: 140px;
  }

  &:hover {
    opacity: 0.8;
    border: 1px solid $primary60-20;
  }
}

.mg12 {
  @media (min-width: $breakpointTablet) {
    margin: 12px 0;
  }
}
</style>
